export type SourceField = 'open' | 'high' | 'low' | 'close' | 'hl2' | 'hlc3' | 'ohlc4' | 'volume'

export const SOURCE_FIELD_VALUES: readonly SourceField[] = [
  'open',
  'high',
  'low',
  'close',
  'hl2',
  'hlc3',
  'ohlc4',
  'volume',
]

// Comparisons yield 1/0 float series; 'and'/'or' treat any nonzero value as
// true and yield 1/0; 'not' yields 1 when its operand is 0, else 0. Absence
// propagation applies unchanged (any absent operand → absent result).
export type CompositeOp =
  | '+'
  | '-'
  | '*'
  | '/'
  | 'min'
  | 'max'
  | 'abs'
  | 'neg'
  | '>'
  | '<'
  | '>='
  | '<='
  | 'and'
  | 'or'
  | 'not'

export type CompositeNodeRef = {
  node: string // id of an EARLIER node (topological order ⇒ no cycles)
  output?: string // required when the referenced node has more than one output
  offset?: number // candles back, 0..50
}

// Chart data needs no declaring step — any operand or indicator input can tap
// a candle field directly, optionally offset candles back.
export type CompositeSourceRef = {
  source: SourceField
  offset?: number // candles back, 0..50
}

export type CompositeRef = CompositeNodeRef | CompositeSourceRef

export type CompositeExpr =
  | { t: 'const'; value: number }
  | { t: 'ref'; ref: CompositeRef }
  | { t: 'op'; op: CompositeOp; args: CompositeExpr[] }

export type CompositeIndicatorNode = {
  id: string
  kind: 'indicator'
  name: string
  params: Record<string, number | string | boolean>
  input?: CompositeRef
}

export type CompositeMathNode = { id: string; kind: 'math'; expr: CompositeExpr }

export type CompositeNode = CompositeIndicatorNode | CompositeMathNode

// Outputs expose computed steps; raw chart data would be pointless to expose
export type CompositeOutput = { field: string; ref: CompositeNodeRef }

export type CompositeDef = {
  v: 1
  id: string
  version: number
  name: string
  nodes: CompositeNode[] // topological order; refs point to earlier nodes only
  outputs: CompositeOutput[]
  minCandles: number
  /** Author-declared display scale (inferred default): 'overlay' = price
   * scale (comparable to price/overlays), 'oscillator' = own scale. */
  scale: 'overlay' | 'oscillator'
}

export type CustomIndicatorDoc = {
  id: string
  name: string
  /** Owner. Indicators live in the TOP-LEVEL `customIndicators/{id}` collection
   * so condition refs resolve by id alone — no owner coupling on patterns. */
  gcid: string
  tradingHouse: string
  active: boolean
  /** 'public' lets other users read the implementation details (full doc +
   * versions); 'private' (default) exposes only the condition-embedded stub. */
  visibility: 'public' | 'private'
  latestVersion: number
  // Firestore stores the def ONLY as its canonical JSON string: a nested def
  // object would breach Firestore's 20-level field-depth cap (expr trees nest
  // 2 levels per depth), and the string keeps cross-language hashing trivially
  // byte-identical. `def` is parsed in memory by useCustomIndicators and never
  // written back.
  defJson: string
  def: CompositeDef
  created: number
  updated: number
}

/**
 * What a pattern condition carries about a custom indicator: an at-a-glance
 * stub (name, outputs, warm-up) with the internals ABSENT. Recipients of
 * shared patterns see what the indicator does, never how; engines fetch the
 * pinned immutable version server-side (`customIndicators/{id}/versions/{v}`)
 * to evaluate. Deterministic per (id, version), so the murmur hash over
 * condition params stays content-addressed.
 */
export type CompositeSnapshot = {
  name: string
  outputs: string[]
  minCandles: number
  scale: 'overlay' | 'oscillator'
}

export const COMPOSITE_LIMITS = {
  maxNodes: 12,
  maxOutputs: 4,
  maxOffset: 50,
  maxExprDepth: 8,
  maxMinCandles: 1000,
  maxNameLength: 40,
  maxPerUser: 50,
} as const

// Names the backend operand fallback resolves BEFORE the indicator hash bag,
// plus 'value': an output named 'value' as fields[0] would hide the composite
// from the LHS picker (ConditionSelector filters fields[0] === 'value').
export const RESERVED_OUTPUT_FIELDS = new Set([
  // 'error' and 'scale' collide with the WASM evaluateComposite result
  // envelope (reserved on every engine in lockstep)
  'error',
  'scale',
  'open',
  'high',
  'low',
  'close',
  'volume',
  'value',
  'index',
  'time',
  'bar_start_datetime',
  'lastTick',
])

export const OUTPUT_FIELD_REGEX = /^[a-zA-Z][a-zA-Z0-9_]{0,30}$/

export const COMPOSITE_NAME_PREFIX = 'composite:'
